/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { storage } from 'utils/storage';

window.ksting = {
  ...window.ksting,
  locale: 'en',
  facebook: {
    appId: 2009340419345314,
  },
};

const { facebook } = window.ksting;

let accessToken = null;

/**
 * Init facebook, create script tag if none exist, or resolve directly.
 *
 * @return {Promise}
 */
const init = data =>
  new Promise(resolve => {
    if (typeof window.FB === 'undefined') {
      window.fbAsyncInit = () => {
        window.FB.init({
          appId: facebook.appId,
          cookie: true,
          xfbml: true,
          version: 'v2.10',
        });

        resolve(data);
      };

      ((d, s, id) => {
        let js = null;
        const fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s);
        js.id = id;
        js.src = '//connect.facebook.net/en_US/sdk.js';
        fjs.parentNode.insertBefore(js, fjs);
      })(document, 'script', 'facebook-jssdk');
    } else {
      resolve(data);
    }
  });

/**
 * Init facebook, create script tag if none exist, or resolve directly.
 *
 * @return {Promise}
 */
const getLoginStatus = () =>
  new Promise((resolve, reject) =>
    window.FB.getLoginStatus(
      response =>
        response.status === 'connected'
          ? resolve(response)
          : reject(response.error)
    )
  );

/**
 * Get facebook accesstoken for logged in user.
 *
 * @return {Promise}
 */
const getAccessToken = () =>
  new Promise((resolve, reject) =>
    window.FB.getAuthResponse(response => {
      if (response.accessToken) {
        accessToken = response.accessToken;
        resolve(response);
      } else {
        reject(response.error);
      }
    })
  );

/**
 * Auth user via facebook.
 *
 * @return {Promise}
 */
const auth = () =>
  new Promise((resolve, reject) => {
    window.FB.login(
      ({ status, error, authResponse }) => {
        if (status === 'connected') {
          storage.write('facebook_token', authResponse.accessToken);
          storage.write('facebook_login_id', authResponse.userID);

          resolve({
            facebook_token: authResponse.accessToken,
            facebook_login_id: authResponse.userID,
          });
        } else {
          reject(error);
        }
      },
      { scope: 'public_profile,email' }
    );
  });

/**
 * Create a post on logged in users wall.
 *
 * @return {Promise}
 */
const share = () =>
  new Promise((resolve, reject) =>
    window.FB.ui(
      {
        method: 'feed',
        // name: p.t('share.name'),
        // link: `http://${location.hostname}${localeSuffix}`,
        // picture: `http://${location.hostname}/${fbPostImage}`,
        // description: p.t('share.description'),
        // caption: p.t('share.caption'),
      },
      response =>
        response && response.post_id ? resolve(response) : reject(response)
    )
  );

/**
 * Fetch public info from users facebook profile.
 *
 * @return {Promise}
 */
const getDetails = (data = {}) =>
  new Promise((resolve, reject) =>
    window.FB.api(
      '/me',
      {
        access_token: accessToken,
        fields: 'id, picture, name, email',
      },
      response => {
        if (response.error) {
          return reject(response);
        }

        const { name, email, picture } = response;

        resolve({
          ...data,
          name,
          email,
          picture: picture && picture.data.url,
        });
      }
    )
  );

/**
 * Handle facebook actions differently depending on action type.
 *
 * @param {string} type
 * @param {object} data
 *
 * @return {Promise}
 */
function callFacebook(type, data) {
  switch (type) {
    case 'init':
      return init();

    case 'auth':
      return init()
        .then(auth, err => {
          throw err;
        })
        .then(getDetails, err => {
          throw err;
        })
        .catch(err => Promise.reject(err));

    case 'share':
      return init(data)
        .then(share, err => {
          throw err;
        })
        .catch(err => Promise.reject(err));

    case 'refresh':
      return init()
        .then(getLoginStatus, err => {
          throw err;
        })
        .then(getAccessToken, err => {
          throw err;
        })
        .then(getDetails, err => {
          throw err;
        })
        .catch(err => Promise.reject(err));

    default:
      return new Promise((resolve, reject) =>
        reject('No such facebook request.')
      );
  }
}

/**
 * FACEBOOK symbol
 *
 * @type {Symbol}
 */
export const FACEBOOK = Symbol('FACEBOOK');

/**
 * Facebook middleware, wraps action with facebook requests. Subsequently triggers actions
 * corresponding to the types passed with the initial action.
 *
 * @return {Promise}
 */
export default () => next => action => {
  const actionData = action[FACEBOOK];

  // Prevent middleware from going past this point if not FACEBOOK is specified.
  if (typeof actionData === 'undefined') {
    return next(action);
  }

  const { type, types, data } = actionData;
  const [requestType, successType, failureType] = types;

  next({ type: requestType });

  return callFacebook(type, data).then(
    response =>
      next({
        type: successType,
        payload: response,
      }),
    errors =>
      next({
        type: failureType,
        payload: errors || 'There was an error.',
      })
  );
};
